import jsPDF from "jspdf";
import "jspdf-autotable";
import TimeStamptoExp from "../timestampConverter";

/** Maximum visible columns supported for readable portrait PDF export. */
export const REPORT_PDF_MAX_COLUMNS = 8;

export const canExportReportPdf = (columnCount: number): boolean =>
  columnCount > 0 && columnCount <= REPORT_PDF_MAX_COLUMNS;

export const getReportPdfDisabledMessage = (columnCount: number): string =>
  `PDF download supports up to ${REPORT_PDF_MAX_COLUMNS} columns. This report has ${columnCount} columns — please use CSV export instead.`;

export type ReportPdfColumn = {
  key: string;
  value: string;
};

export type ExportReportTablePdfOptions = {
  fileName: string;
  reportTitle: string;
  columns: ReportPdfColumn[];
  rows: Record<string, unknown>[];
  formatCell: (key: string, value: unknown) => string;
  isDateField?: (key: string) => boolean;
};

export function exportReportTablePdf({
  fileName,
  reportTitle,
  columns,
  rows,
  formatCell,
  isDateField,
}: ExportReportTablePdfOptions): void {
  const colCount = columns.length;
  if (!canExportReportPdf(colCount) || rows.length === 0) return;

  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  const usableWidth = pageWidth - margin * 2;

  doc.setFontSize(16);
  doc.text(reportTitle, margin, 20);
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  doc.text(`Generated: ${new Date().toLocaleString()}`, margin, 27);
  doc.setTextColor(0, 0, 0);

  const headerLabels = columns.map((col) => col.value);
  const body = rows.map((row) =>
    columns.map((col) => {
      const value = row[col.key];
      if (value === null || value === undefined) return "-";
      if (isDateField?.(col.key) && typeof value === "number") {
        return TimeStamptoExp.formatDateFromTimestamp(value);
      }
      return formatCell(col.key, value);
    }),
  );

  const firstColWidth =
    colCount > 1
      ? Math.min(usableWidth * 0.32, (usableWidth / colCount) * 1.5)
      : usableWidth;
  const restWidth =
    colCount > 1 ? (usableWidth - firstColWidth) / (colCount - 1) : usableWidth;
  const columnStyles: Record<
    number,
    { cellWidth: number; halign: "left" | "center" }
  > = {};
  for (let i = 0; i < colCount; i++) {
    columnStyles[i] = {
      cellWidth: i === 0 ? firstColWidth : restWidth,
      halign: i === 0 ? "left" : "center",
    };
  }

  (doc as any).autoTable({
    head: [headerLabels],
    body,
    startY: 34,
    margin: { left: margin, right: margin },
    tableWidth: usableWidth,
    styles: {
      fontSize: colCount > 6 ? 7 : 8,
      cellPadding: 2.5,
      overflow: "linebreak",
      valign: "middle",
    },
    headStyles: {
      fillColor: [0, 0, 0],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      halign: "center",
      overflow: "linebreak",
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245],
    },
    columnStyles,
  });

  const pageCount = (doc as any).internal.pages.length - 1;
  doc.setFontSize(9);
  doc.setTextColor(150, 150, 150);
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, {
      align: "center",
    });
  }

  doc.save(fileName);
}
